import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { customCollectionsAPI, CustomCollection } from "../services/api";
import { useAuth0 } from "@auth0/auth0-react";

const Dashboard: React.FC = () => {
  const [collections, setCollections] = useState<CustomCollection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const customCollectionsRes = await customCollectionsAPI.getCollections(
          getAccessTokenSilently
        );

        setCollections(customCollectionsRes.data.collections || []);
      } catch (err: any) {
        setError("Failed to load collections. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getAccessTokenSilently]);

  const handleStartPractice = async (customCollectionId: number) => {
    try {
      const response = await customCollectionsAPI.getPracticeInfo(
        customCollectionId,
        getAccessTokenSilently
      );
      const collectionId = response.data.collection_id;
      navigate(`/practice/${collectionId}`);
    } catch (err: any) {
      setError("Failed to start practice. Please try again.");
      console.error(err);
    }
  };

  const handleStartVerbPractice = async (customCollectionId: number) => {
    try {
      const response = await customCollectionsAPI.getPracticeInfo(
        customCollectionId,
        getAccessTokenSilently
      );
      const collectionId = response.data.collection_id;
      navigate(`/verbs/${collectionId}`);
    } catch (err: any) {
      setError("Failed to start verb practice. Please try again.");
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
        <button
          onClick={() => window.location.reload()}
          className="bg-cyan-600 text-white px-4 py-2 rounded hover:bg-cyan-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  // Filter out collections with no verses
  const filteredCollections = collections.filter(
    (collection) => collection.verse_count && collection.verse_count > 0
  );

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-zinc-900 dark:text-zinc-100 mb-2">
            Practice
          </h1>
          <p className="text-zinc-600 dark:text-zinc-400">
            Choose a deck below to practice Koine Greek clozes and verb parsing.
          </p>
        </div>
        <Link
          to="/decks"
          className="bg-teal-600 hover:bg-teal-700 text-white py-2 px-4 rounded-md transition-colors"
        >
          Manage
        </Link>
      </div>

      {filteredCollections.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-zinc-500 dark:text-zinc-400 mb-4">
            <svg
              className="mx-auto h-12 w-12"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-2">
            You don't have any decks with verses yet
          </h3>
          <p className="text-zinc-500 dark:text-zinc-400 mb-4">
            Create a deck and add verses to get started.
          </p>
          <Link
            to="/decks"
            className="inline-block bg-teal-600 hover:bg-teal-700 text-white py-2 px-6 rounded-md transition-colors"
          >
            Go to Deck Management →
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCollections.map((collection) => (
            <div
              key={collection.id}
              className="bg-white dark:bg-zinc-800 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 border border-zinc-200 dark:border-zinc-700 hover:border-teal-700 dark:hover:border-teal-700 p-6 relative hover:-translate-y-1 flex flex-col"
            >
              {/* Progress Badge */}
              {collection.progress !== undefined && collection.progress > 0 && (
                <div className="absolute top-4 right-4 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 px-3 py-1 rounded-full text-sm font-semibold z-10">
                  {collection.progress}%
                </div>
              )}

              {/* Public Badge */}
              {collection.is_public && (
                <div className="absolute top-4 left-4 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 px-3 py-1 rounded-full text-xs font-semibold z-10">
                  Public
                </div>
              )}

              <div className="flex-1">
                <h3
                  className={`text-xl font-semibold text-zinc-900 dark:text-zinc-100 mb-2 pr-16 ${
                    collection.is_public ? "pl-20" : ""
                  }`}
                >
                  {collection.name}
                </h3>

                {collection.description && (
                  <p className="text-zinc-600 dark:text-zinc-400 mb-4 line-clamp-3">
                    {collection.description}
                  </p>
                )}

                <div className="flex justify-between items-center mb-4">
                  <span className="text-sm text-zinc-500 dark:text-zinc-400">
                    {collection.verse_count} verses
                  </span>
                </div>
              </div>

              {/* Practice Buttons */}
              <div className="space-y-2">
                {(collection.exercise_type === "clozes" ||
                  collection.exercise_type === "both" ||
                  !collection.exercise_type) && (
                  <button
                    onClick={() => handleStartPractice(collection.id)}
                    className="w-full bg-teal-600 hover:bg-teal-700 text-white py-2 px-4 rounded-lg transition-all text-center font-medium"
                  >
                    Practice Clozes
                  </button>
                )}
                {(collection.exercise_type === "verbs" ||
                  collection.exercise_type === "both" ||
                  !collection.exercise_type) && (
                  <button
                    onClick={() => handleStartVerbPractice(collection.id)}
                    className="w-full bg-cyan-600 hover:bg-cyan-700 text-white py-2 px-4 rounded-lg transition-all text-center font-medium"
                  >
                    Practice Verbs
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
